import React, { FC, useState, useEffect, useCallback } from 'react';
import Grid, { GridItem } from 'src/components/Grid';
import Select, { OptionType, SelectValueType } from 'src/components/Select';
import translate from '@translate';
import { UserProviderType, UserType } from 'src/types/user';
import { ProviderType } from 'src/types/provider';
import { FormErrorsType, OnChangeType } from '../../types/global';
import { EditKeysType } from './constants';
import { hasAllActiveProviders } from './useAllProvidersField';

export interface UserProvidersPropsType {
    onChange: OnChangeType<EditKeysType>;
    editData: UserType;
    editErrors: FormErrorsType;
    oreginalData: UserType | null;
    disableEdit: boolean;
    providersListData: ProviderType[];
    allProviders: number;
    setAllProviders: (value: number) => void;
    isSkladonUser: boolean;
}

const getActiveProvidersSorted = (providersListData: ProviderType[]): ProviderType[] =>
    providersListData
        .filter((provider) => provider.ACTIVE === 1)
        .sort((a, b) => (a.COMPANY_NAME || '').localeCompare(b.COMPANY_NAME || '', 'cs'));

const UserProviders: FC<UserProvidersPropsType> = (props) => {
    const [providersOptions, setProvidersOptions] = useState<OptionType[]>([]);
    const [providers, setProviders] = useState<SelectValueType[]>([]);
    const [notificateProvidersOptions, setNotificateProvidersOptions] = useState<OptionType[]>([]);
    const [notificateProviders, setNotificateProviders] = useState<SelectValueType[]>([]);

    useEffect(() => {
        if (props.providersListData) {
            setProvidersOptions(
                getActiveProvidersSorted(props.providersListData).map((provider) => ({
                    value: provider.ESHOP_KEY,
                    label: provider.COMPANY_NAME || '',
                }))
            );
        }
    }, [props.providersListData]);

    useEffect(() => {
        if (!providersOptions.length) {
            return;
        }

        const userProviders = props.editData.USER_PROVIDERS ?? [];
        const settedProviders: string[] = userProviders.map((provider) => provider.PROVIDER_ESHOP_KEY);
        setProviders(settedProviders);
        setNotificateProvidersOptions(
            providersOptions.filter((providerOption) =>
                settedProviders.find((settedProvider) => settedProvider === providerOption.value)
            )
        );
        const settedNotificateProviders = userProviders
            .filter((provider) => provider.NOTIFICATION_ACTIVE)
            .map((provider) => provider.PROVIDER_ESHOP_KEY);
        setNotificateProviders(settedNotificateProviders);
    }, [props.editData.USER_PROVIDERS, providersOptions]);

    const onChangeProviders = useCallback(
        (value: SelectValueType | SelectValueType[], error: boolean) => {
            if (!Array.isArray(value)) {
                return;
            }

            const currentUserProviders = props.editData.USER_PROVIDERS ?? [];
            const sourceUserProviders = props.oreginalData?.USER_PROVIDERS ?? currentUserProviders;

            let updatedUserProviders: UserProviderType[] = [...currentUserProviders];
            if (value.length < currentUserProviders.length) {
                updatedUserProviders = updatedUserProviders.filter((userProvider) =>
                    value.find((eshopKey) => eshopKey === userProvider.PROVIDER_ESHOP_KEY)
                );
            } else {
                const newUsers = value.filter(
                    (eshopKey) =>
                        !updatedUserProviders.find((userProvider) => eshopKey === userProvider.PROVIDER_ESHOP_KEY)
                );
                newUsers.forEach((eshopKey) => {
                    const oldProvider = sourceUserProviders.find(
                        (userProvider) => userProvider.PROVIDER_ESHOP_KEY === eshopKey
                    );
                    if (oldProvider) {
                        updatedUserProviders.push({ ...oldProvider });
                    } else {
                        updatedUserProviders.push({
                            PROVIDER_ESHOP_KEY: String(eshopKey),
                            NOTIFICATION_ACTIVE: 1,
                        });
                    }
                });
            }

            props.onChange('USER_PROVIDERS', updatedUserProviders, error);

            if (props.isSkladonUser && props.allProviders === 1) {
                const activeProviderKeys = providersOptions.map((option) => String(option.value));
                if (!hasAllActiveProviders(updatedUserProviders, activeProviderKeys)) {
                    props.setAllProviders(0);
                }
            }
        },
        [
            props.onChange,
            props.editData.USER_PROVIDERS,
            props.oreginalData?.USER_PROVIDERS,
            props.isSkladonUser,
            props.allProviders,
            props.setAllProviders,
            providersOptions,
        ]
    );

    const onChangeNotificateProviders = useCallback(
        (value: SelectValueType | SelectValueType[], error: boolean) => {
            if (!Array.isArray(value)) {
                return;
            }
            const currentUserProviders = props.editData.USER_PROVIDERS ?? [];
            const updatedUserProviders: UserProviderType[] = currentUserProviders.map((userProvider) => ({
                ...userProvider,
                NOTIFICATION_ACTIVE: value.find((eshopKey) => eshopKey === userProvider.PROVIDER_ESHOP_KEY) ? 1 : 0,
            }));

            props.onChange('USER_PROVIDERS', updatedUserProviders, false);
        },
        [props.onChange, props.editData.USER_PROVIDERS]
    );

    return (
        <Grid>
            <GridItem cols={12}>
                <Select
                    label={translate('userDetail.providers')}
                    value={providers}
                    options={providersOptions}
                    onChange={onChangeProviders}
                    name="USER_PROVIDERS"
                    error={props.editErrors.USER_PROVIDERS}
                    disabled={props.disableEdit}
                    limitTags={5}
                    fullWidth
                    multiple
                    expandList
                    required
                />
                <Select
                    label={translate('userDetail.providersNotificate')}
                    value={notificateProviders}
                    options={notificateProvidersOptions}
                    onChange={onChangeNotificateProviders}
                    name="providersNotificate"
                    disabled={props.disableEdit}
                    limitTags={5}
                    fullWidth
                    multiple
                    expandList
                />
            </GridItem>
        </Grid>
    );
};

export default UserProviders;
